import { getValuation, getValuations } from 'ClientPortal/src/api/valuationRequests'
import { skipToken, useMutation, useQueries, useQuery } from '@tanstack/react-query'
import { getValuationApiClient } from 'ClientPortal/src/api/valuationApiClient'
import { useFetchNewReportUrlFromValuation } from 'ClientPortal/src/api/Queries/orders'
import { useGlobalMutationErrorHandler } from 'ClientPortal/src/api/useGlobalMutationErrorHandler'
import { useCallback } from 'react'
import {
    joinDefinedStrings,
    ReactQueryOptions,
    sendDebugInfoToSentry,
    useDownloadFileParameterized,
    WithReactQueryOptions,
} from 'ComponentLibrary'
import { getAssetAddressSlug } from 'ClientPortal/src/common/utils/valuationFiles'
import { useGetAsset } from 'ClientPortal/src/api/Queries/assets'
import type { CustomerExistingValuation as ExistingValuation } from '@einwert/valuation-api-client'
import invariant from 'tiny-invariant'
import dayjs from 'dayjs'
import { ExistingAsset, ReadValueCheck } from '@einwert/valuation-api-client'
import { useTranslation } from 'react-i18next'
import { getSelectedCustomer } from 'ClientPortal/src/context/SelectedCustomerContext'

export const VALUATION_KEYS = {
    all: ['VALUATION'],
    lists: () => [...VALUATION_KEYS.all, 'list'],
    list: (filters: Record<string, unknown>) => [...VALUATION_KEYS.lists(), { filters }],
    single: (valuationId: string | undefined) => [...VALUATION_KEYS.all, valuationId],
}

function withCalculatedValuationMetrics(valuation: ExistingValuation): ExistingValuation {
    if (valuation.valuation_type === 'einwert') {
        return valuation
    }

    return {
        ...valuation,
        gross_yield: valuation.calculated_gross_yield,
        gross_initial_yield: valuation.calculated_gross_initial_yield,
        net_yield: valuation.calculated_net_yield,
        net_initial_yield: valuation.calculated_net_initial_yield,
        cap_rate: valuation.calculated_cap_rate,
        gross_yield_multiplier: valuation.calculated_gross_yield_multiplier,
        net_yield_multiplier: valuation.calculated_net_yield_multiplier,
    }
}

function useGetValuation({ valuationId, ...options }: WithReactQueryOptions<{ valuationId: string | undefined }>) {
    const { data, isPending } = useQuery({
        queryKey: VALUATION_KEYS.single(valuationId),
        queryFn: valuationId ? () => getValuation({ valuationId }) : skipToken,
        select: withCalculatedValuationMetrics,
        ...options,
    })

    return { valuation: data, isValuationPending: isPending }
}

function useGetValuationsByIds({ valuationIds }: { valuationIds?: string[] }) {
    return useQueries({
        queries:
            valuationIds?.map((valuationId) => ({
                queryKey: VALUATION_KEYS.single(valuationId),
                queryFn: () => getValuation({ valuationId }),
                select: withCalculatedValuationMetrics,
            })) ?? [],
    })
}

function useGetValuations({
    assetId,
    includeDrafts,
    filterFinalized = true,
}: {
    assetId?: string
    includeDrafts?: boolean
    filterFinalized?: boolean
}) {
    const searchParams: { [key: string]: unknown } = {}
    searchParams.assetId = assetId
    if (includeDrafts) {
        searchParams.includeDrafts = includeDrafts
    }

    const { data, isPending, isError } = useQuery({
        queryKey: VALUATION_KEYS.list(searchParams),
        queryFn: () => getValuations(searchParams),
        enabled: !!assetId,
        select: (data) =>
            (filterFinalized
                ? data.valuations.filter((valuation) => valuation.status === 'finalised' || (includeDrafts && valuation.is_draft))
                : data.valuations
            ).map(withCalculatedValuationMetrics),
    })

    return { valuations: data || [], isValuationsPending: isPending, isValuationsError: isError }
}

function useFetchValuationsForAssets() {
    const handleError = useGlobalMutationErrorHandler()

    return useMutation({
        mutationFn: async ({ assetIds }: { assetIds: string[] }) => {
            const valuationPromises = assetIds.map((assetId) => getValuations({ assetId, includeDrafts: false }))
            const valuationResponses = await Promise.all(valuationPromises)
            return valuationResponses.flatMap((response) =>
                response.valuations.filter((valuation) => valuation.status === 'finalised').map(withCalculatedValuationMetrics)
            )
        },
        onError: handleError,
    })
}

type ReportFileNameLabels = {
    draft: string
    valuationReport: string
    valueCheck: string
}

const getDraftName = (valuation: ExistingValuation, labels: ReportFileNameLabels): string => {
    const valuationTitle = valuation.title
    if (!valuationTitle) {
        return labels.draft
    }
    return valuationTitle.split(' - ')[0].replace(/\s+/g, '_')
}

const getReportFileName = (valuation: ExistingValuation, asset: ExistingAsset, labels: ReportFileNameLabels): string => {
    const valuationDate = valuation.valuation_date
    invariant(valuationDate, 'A client facing valuation must have a valuation date')

    const valuationDateNewFormat = dayjs(valuationDate).format('YYYY_MM_DD')

    if (!valuation.is_draft) {
        return `${valuationDateNewFormat}_${labels.valuationReport}_${getAssetAddressSlug(asset.address)}`
    }

    return `${valuationDateNewFormat}_${getDraftName(valuation, labels)}_${getAssetAddressSlug(asset.address)}`
}

const getValueCheckReportFileName = (valueCheck: ReadValueCheck, asset: ExistingAsset, labels: ReportFileNameLabels): string => {
    const valueCheckDate = valueCheck.valuation_date
    const valueCheckDateNewFormat = valueCheckDate ? dayjs(valueCheckDate).format('YYYY_MM_DD') : undefined
    const prefix = joinDefinedStrings([valueCheckDateNewFormat, labels.valueCheck], '_') || labels.valueCheck
    return `${prefix}_${getAssetAddressSlug(asset.address)}`
}

const useDownloadReport = (assetId: string | undefined) => {
    const { t: tCommon } = useTranslation('terminology', { keyPrefix: 'common' })
    const { fetchNewReportUrl, isFetchingNewReportUrl } = useFetchNewReportUrlFromValuation()
    const valuationApiClient = getValuationApiClient()
    const customerId = getSelectedCustomer()?.uuid
    const handleError = useGlobalMutationErrorHandler()
    const { mutateAsync: fetchNewValueCheck, isPending: isFetchingNewValueCheck } = useMutation({
        mutationFn: ({ valueCheckId }: { valueCheckId: string }) =>
            valuationApiClient.valueChecks.getValueCheck({ valueCheckId, customerId }),
        onError: handleError,
    })
    const { asset, isAssetPending } = useGetAsset({ assetId })
    const downloadFile = useDownloadFileParameterized()
    const downloadReport = useCallback(
        async ({ valuationId, valueCheckId }: { valuationId?: string; valueCheckId?: string }) => {
            const reportFileNameLabels: ReportFileNameLabels = {
                draft: tCommon('draft'),
                valuationReport: tCommon('valuationReport'),
                valueCheck: tCommon('valueCheck'),
            }

            if (valueCheckId) {
                const valueCheck = await fetchNewValueCheck({ valueCheckId })
                if (valueCheck.report_file) {
                    try {
                        await waitForReadyConditionsUntilTimeout([() => !isAssetPending])
                        invariant(asset, 'Asset must be defined to download a report')

                        const customName = getValueCheckReportFileName(valueCheck, asset, reportFileNameLabels)
                        await downloadFile({
                            url: valueCheck.report_file.url,
                            name: customName,
                            content_type: valueCheck.report_file.content_type,
                        })
                    } catch (e) {
                        sendDebugInfoToSentry(e)
                        window.open(valueCheck.report_file.url, '_blank')
                    }
                }
                return
            }

            if (valuationId) {
                // Since the signed report url is only valid for a few minutes we need to re-fetch it first
                const valuation = await fetchNewReportUrl({ valuationId })
                if (valuation?.report_file?.url) {
                    try {
                        await waitForReadyConditionsUntilTimeout([() => !isAssetPending])
                        invariant(asset, 'Asset must be defined to download a report')

                        const customName = getReportFileName(valuation, asset, reportFileNameLabels)
                        await downloadFile({
                            url: valuation.report_file.url,
                            name: customName,
                            content_type: valuation.report_file.content_type,
                        })
                    } catch (e) {
                        sendDebugInfoToSentry(e)
                        window.open(valuation.report_file.url, '_blank')
                    }
                }
            }
        },
        [asset, downloadFile, fetchNewReportUrl, fetchNewValueCheck, isAssetPending, tCommon]
    )

    return { downloadReport, isDownloadingReport: isFetchingNewReportUrl || isFetchingNewValueCheck || isAssetPending }
}

type DownloadValXmlVariables = { valuationId: string }

const useDownloadValXml = () => {
    const handleError = useGlobalMutationErrorHandler()
    const { mutateAsync: downloadValXml, isPending: isDownloadingValXml } = useMutation({
        mutationFn: ({ valuationId }: DownloadValXmlVariables) =>
            getValuationApiClient().valuations.getValXmlExportLink({ valuationId, customerId: getSelectedCustomer()?.uuid }),
        onSuccess: (url) => {
            window.open(url, '_self')
        },
        onError: handleError,
    })

    return { downloadValXml, isDownloadingValXml }
}

const useDownloadValXmls = ({ assetId, onSuccess, onError }: { assetId: string } & ReactQueryOptions<string>) => {
    const handleError = useGlobalMutationErrorHandler(onError)

    const { mutateAsync, isPending } = useMutation({
        mutationFn: () => getValuationApiClient().assets.getAllAssetValXMLsLinkToZip({ assetId, customerId: getSelectedCustomer()?.uuid }),
        onSuccess,
        onError: handleError,
    })

    return { downloadValXmls: mutateAsync, isDownloadingValXmls: isPending }
}

const waitForReadyConditionsUntilTimeout = (conditions: (() => boolean)[], intervalMs = 50, timeoutMs = 5000): Promise<void> => {
    return new Promise((resolve, reject) => {
        const start = Date.now()

        const check = () => {
            const allReady = conditions.every((cond) => cond())
            if (allReady) {
                resolve()
            } else if (Date.now() - start > timeoutMs) {
                reject(new Error('Timeout waiting for conditions'))
            } else {
                setTimeout(check, intervalMs)
            }
        }

        check()
    })
}

export {
    withCalculatedValuationMetrics,
    useGetValuation,
    useGetValuationsByIds,
    useDownloadReport,
    useDownloadValXml,
    useDownloadValXmls,
    useGetValuations,
    useFetchValuationsForAssets,
}
